import useFeatureFlag from '../../common/use-feature-flag';
import DEFAULT_AVATAR_NEW from '../advocate/avatar';
import { Feature } from '../features/features';
import { Content } from './types';

// function typing at utils.js are weird - we can merge these two modules once utils.js are typed

export function getContentAuthorName(content: Content) {
  return content.source_name;
}

export function getContentAuthorAvatarSrc(content: Content) {
  return (
    content?.source_avatar?.url ||
    (content.submitter_id ? DEFAULT_AVATAR_NEW : content.source_image_url)
  );
}

export function getContentAuthorAvatarColor(content: Content) {
  const isSubmitted = getContentIsUserSubmitted(content);
  return isSubmitted ? content.source_avatar?.color : undefined;
}

export function getContentAuthorProfilePath(authorId: string | number) {
  return `/profiles/${authorId}`;
}

// Alias types that represent a shared group identity (rather than a single user).
// Content published under these routes to the alias profile page; the individual
// publisher's identity stays hidden.
export const GROUP_ALIAS_TYPES = ['team', 'program'] as const;

// Computes the href a content author should link to. Reads
// Feature.AUTHOR_ALIAS_NAV_FIX internally so callers don't thread the flag
// value through — when the flag is off, group-alias branching is skipped and
// behavior reverts to /profiles/:author_id (pre-ticket).
// The 'deleted' branch is unconditional regardless of flag state: it's a
// privacy guard. Pre-ticket, `alias_type === 'deleted'` didn't exist as a
// discriminator (Pony only began emitting it on this branch), so this path
// only fires when the backend serializer already decided to mark the alias
// as deleted — at which point we never want to fall back to the publisher's
// profile, on or off.
export function useContentAuthorLink(
  content:
    | Pick<Content, 'author_id' | 'author_alias_id' | 'alias_type'>
    | null
    | undefined
): string | null {
  const authorAliasNavEnabled = !!useFeatureFlag(Feature.AUTHOR_ALIAS_NAV_FIX);

  if (!content) return null;

  if (
    authorAliasNavEnabled &&
    content.alias_type &&
    (GROUP_ALIAS_TYPES as readonly string[]).includes(content.alias_type)
  ) {
    return content.author_alias_id
      ? `/aliases/${content.author_alias_id}`
      : null;
  }
  if (content.alias_type === 'deleted') {
    return null;
  }
  const authorId = Number(content.author_id);
  if (Number.isInteger(authorId) && authorId > 0) {
    return `/profiles/${authorId}`;
  }
  return null;
}

export function getContentDetailPath(contentId: string | number) {
  return `/contents/${contentId}`;
}

export function getContentIsUserSubmitted(content: Content) {
  return content.source_type === 'submitted';
}

// Owner-only management actions (Duplicate/Archive) stay hidden unless the
// current advocate created the post. created_by is the true creator even for
// alias/group-authored posts (author_id is the alias there) and matches what
// the v2 content_submissions endpoints enforce. A null created_by (legacy or
// not-yet-serialized) fails closed: the actions stay hidden.
export function contentIsOwnedBy(
  createdBy: number | null | undefined,
  advocateId: number | null | undefined
): boolean {
  return createdBy != null && advocateId != null && createdBy === advocateId;
}
